/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { TranslocoModule } from '@jsverse/transloco';
import { merge } from 'lodash';

import { DialogBase, DialogFormData } from '../../../interfaces';
import { InputNumberComponent } from '../../fields';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';

@Component({
  selector: 'hmax-dialog-from-data',
  imports: [DialogBaseComponent, TranslocoModule, MatDialogModule, ReactiveFormsModule, InputNumberComponent],
  templateUrl: './dialog-from-data.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-from-data.component.scss',
})
export class DialogFromDataComponent {
  private _dialogRef = inject<MatDialogRef<DialogFromDataComponent, Record<string, any>>>(MatDialogRef);
  private _formBuilder = inject(FormBuilder);
  readonly data = inject<DialogFormData>(MAT_DIALOG_DATA);

  readonly form = signal(this._formBuilder.group({}));

  readonly isInvalid = computed(() => this.form().invalid);

  constructor() {
    if (!this.data?.fields?.length) {
      return;
    }

    const controls = Object.fromEntries(this.data.fields.map((field) => [field.key, ['', field.validators ?? []]]));

    this.form.set(this._formBuilder.group(controls));

    if (this.data.initialValue) {
      this.form().patchValue(this.data.initialValue);
    }
  }

  /**
   * Abrir um modal deste componente com as configurações padrão
   * Precisa receber uma instância do MatDialog
   * ex: DialogFromDataComponent.open(this._matDialog);
   * @param matDialog Instância de MatDialog do Angular Material que deve ser injetada
   * pela classe que invocar este método
   */
  static open<T extends Record<string, any>>(matDialog: MatDialog, data: DialogFormData<T>): MatDialogRef<DialogFromDataComponent, T> {
    const defaultConfig: DialogBase = {
      actions: {
        confirm: {
          color: 'primary',
          label: 'save',
          labelTranslate: true,
          show: true,
          action: () => dialogRef.componentInstance.save(),
        },
        cancel: {
          label: 'cancel',
          labelTranslate: true,
          show: true,
        },
      },
    };

    const config = merge({}, defaultConfig, data);

    const dialogRef = matDialog.open<DialogFromDataComponent, DialogFormData<T>, T>(DialogFromDataComponent, {
      autoFocus: true,
      disableClose: false,
      data: config,
    });

    return dialogRef;
  }

  save(): void {
    if (this.form().invalid) {
      this.form().markAllAsTouched();
      this.form().markAsDirty();
      return;
    }

    this._dialogRef.close(this.form().getRawValue());
  }
}
